import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {CreateBoxDialogComponent} from '../create-box-dialog/create-box-dialog.component';
import {MatTableDataSource} from '@angular/material/table';
import {switchMap, tap} from 'rxjs/operators';
import {EMPTY, Observable, of} from 'rxjs';
import {MatPaginator} from '@angular/material/paginator';
import {Sort} from "@angular/material/sort";
import {Box} from "../../../model/box";
import {DialogsService} from "../../../services/dialogs.service";
import {SnackService} from "../../../services/snack.service";
import {
  GeneralItemDetailDialogComponent
} from "../../../shared-components/general-item-detail-dialog/general-item-detail-dialog.component";
import {BoxesService} from "../../../services/boxes-service";
import {Contract} from "../../../model/contract";
import {Constants} from "../../../utils/constants";
import {AddResearchersDialogComponent} from "../add-researchers-dialog/add-researchers-dialog.component";
import {BoxDialogComponent} from "../box-dialog/box-dialog.component";
import {AuthService} from "../../../services/auth.service";
import {AddBoxInterfaceDialogComponent} from "../add-box-interface-dialog/add-box-interface-dialog.component";
import {AddSimDialogComponent} from "../add-sim-dialog/add-sim-dialog.component";
import {SimsService} from "../../../services/sims.service";
import {StatusLegendDialogComponent} from "../../../shared-components/status-dialog/status-legend-dialog.component";
import {BoxInterfaceInfoDialogComponent} from '../box-interface-info-dialog/box-interface-info-dialog.component';
import {hasPermission, User} from "../../../model/user";
import {UsersService} from "../../../services/users.service";


@Component({
  selector: 'aw-list-boxes',
  templateUrl: './list-boxes.component.html',
  styleUrls: ['./list-boxes.component.scss']
})
export class ListBoxesComponent implements OnInit, AfterViewInit {

  public displayedColumns = [
    'identifier',
    'hardwareVersion',
    'softwareVersion',
    'server',
    'logisticsStatus',
    'sim',
    'enabled',
    'action'
  ];
  disabled = false;
  disabledItemId: number[] = [];
  loading = false;
  iAmCustomer = false;
  iAmAdmin = false;
  @ViewChild('paginatorBoxes') paginatorUsers!: MatPaginator;
  @Input() contract?: Contract;
  @Input() embeddedLayout = false;

  dataSource!: MatTableDataSource<Box>;
  expandedBoxEventsHistory?: Box;
  expandedResearchers?: Box;

  expandedInterfaces?: Box;
  expandedSim?: Box;
  entities = Constants.entities;
  contractStatus = Constants.contractStatuses;
  boxStatus = Constants.boxStatuses;
  loggedUser?: User;
  protected readonly hasPermission = hasPermission;
  private allBoxes$!: Observable<Box[]>;

  constructor(
    public dialog: MatDialog,
    public dialogsService: DialogsService,
    public authService: AuthService,
    public boxesService: BoxesService,
    public usersService: UsersService,
    private simsService: SimsService,
    private snackService: SnackService
  ) {
  }

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser();
    this.iAmCustomer = hasPermission(this.loggedUser, "customer");
    this.iAmAdmin = hasPermission(this.loggedUser, "admin");
  }

  ngAfterViewInit(): void {
    this.allBoxes$ =
      of(true)
        .pipe(
          tap(() => this.loading = true),
          switchMap(() => {
            if ((this.iAmCustomer && !this.iAmAdmin) || this.contract)
              return this.boxesService.getAllBoxesByContract(this.contract);

            return this.boxesService.getAllBoxes();
          }),
          tap(boxes => {
            this.dataSource = new MatTableDataSource<Box>(boxes);
            this.dataSource.paginator = this.paginatorUsers;

            this.loading = false;
          })
        );
    this.allBoxes$.subscribe();
  }

  expandEvents(element: Box): void {
    this.expandedBoxEventsHistory != element ?
      this.expandedBoxEventsHistory = element :
      this.expandedBoxEventsHistory = undefined;

    this.expandedSim = undefined;
    this.expandedResearchers = undefined;
    this.expandedInterfaces = undefined;
  }

  expandResearchers(element: Box): void {
    this.expandedResearchers != element ?
      this.expandedResearchers = element :
      this.expandedResearchers = undefined;

    this.expandedSim = undefined;
    this.expandedInterfaces = undefined;
    this.expandedBoxEventsHistory = undefined;
  }

  expandBoxInterfaces(element: Box): void {
    this.expandedInterfaces != element ?
      this.expandedInterfaces = element :
      this.expandedInterfaces = undefined;

    this.expandedSim = undefined;
    this.expandedResearchers = undefined;
    this.expandedBoxEventsHistory = undefined;
  }

  expandSim(element: Box): void {
    this.expandedSim != element ?
      this.expandedSim = element :
      this.expandedSim = undefined;

    this.expandedInterfaces = undefined;
    this.expandedResearchers = undefined;
    this.expandedBoxEventsHistory = undefined;
  }

  applyFilters(event: string): void {
    this.dataSource.filter = event.trim().toLowerCase();
  }

  doSortBoxes(event: Sort): void {
    this.dataSource.paginator?.firstPage();
    this.dataSource.data = this.dataSource.data.sort((a, b) => {
      const index: string = event.active;

      // Handle nested properties
      let aValue: any;
      let bValue: any;
      switch (index) {
        case 'server':
          aValue = a.boxLocation?.region || '';
          bValue = b.boxLocation?.region || '';
          break;
        case 'logisticsStatus':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'sim':
          aValue = a.sim?.identifier || '';
          bValue = b.sim?.identifier || '';
          break;
        case 'certificate':
          aValue = a.currentCertificate?.endDate || '';
          bValue = b.currentCertificate?.endDate || '';
          break;
        default:
          aValue = (a as any)[index] || '';
          bValue = (b as any)[index] || '';
      }

      if (event.direction === 'asc') {
        return ('' + bValue).localeCompare('' + aValue);
      } else {
        return ('' + aValue).localeCompare('' + bValue);
      }
    });
  }

  details(box: Box): void {
    this.dialog.open(GeneralItemDetailDialogComponent, {data: box});
  }

  createBox(): void {
    this.disabled = true;
    this.dialog
      .open(CreateBoxDialogComponent, {data: new Box()})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allBoxes$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  editBox(box: Box): void {
    this.disabled = true;
    this.dialog
      .open(CreateBoxDialogComponent, {data: box})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allBoxes$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  deleteBox(box: Box): void {
    this.disabled = true;
    const id = box.id ?? -1;
    this.disabledItemId.push(id);
    this.dialogsService
      .confirm('Delete box', `Are you sure you want to delete the box "${box.identifier}"?`)
      .pipe(
        switchMap((value) => value ?
          this.boxesService.deleteBox(<number>box.id) :
          EMPTY),
        switchMap(() => this.allBoxes$)
      )
      .subscribe(() => {
          this.disabled = false;
          this.snackService.success('Box deleted!');
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        }, (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        },
        () => {
          this.disabled = false;
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        });

  }

  addResearchers(box: Box): void {
    this.disabled = true;
    // if(this.expandedResearchers){
    //   this.expandedResearchers = undefined;
    // }

    this.dialog
      .open(AddResearchersDialogComponent, {data: box})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allBoxes$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
          // this.expandedResearchers = box;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });

  }

  addBoxInterfaces(box: Box): void {
    this.disabled = true;
    this.dialog
      .open(AddBoxInterfaceDialogComponent, {data: box})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allBoxes$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });

  }

  performAction(box: Box, enabled: boolean): void {
    this.disabled = true;
    this.dialog.open(BoxDialogComponent, {data: {box: box, enable: enabled}})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allBoxes$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  assignSim(box: Box): void {
    this.disabled = true;
    this.dialog
      .open(AddSimDialogComponent, {data: box})
      .afterClosed()
      .pipe(switchMap(value => value ? this.allBoxes$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });

  }

  removeSim(box: Box): void {
    this.disabled = true;
    this.dialogsService
      .confirm('Remove sim', `Are you sure you want to remove the sim "${box.sim?.identifier}" from the box "${box?.identifier}"?`)
      .pipe(
        switchMap((value) => value ? this.simsService.deleteSimAssociation(<number>box.id) : EMPTY),
        switchMap(() => this.allBoxes$)
      )
      .subscribe(() => {
          this.disabled = false;
          this.snackService.success('Sim removed!');
        }, (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        },
        () => {
          this.disabled = false;
        });

  }

  getTooltip(box: Box): string {
    switch (box.status) {
      case Constants.boxStatuses.onTheShelf:
        return Constants.boxStatusesExplainedForLegend.onTheShelf;
      case Constants.boxStatuses.toBeConfigured:
        return Constants.boxStatusesExplainedForLegend.toBeConfigured;
      case Constants.boxStatuses.broken:
        return Constants.boxStatusesExplainedForLegend.broken;
      case Constants.boxStatuses.updating:
        return Constants.boxStatusesExplainedForLegend.updating;
      case Constants.boxStatuses.assigned:
        return `The box has been assigned to the contract "${box.contract?.title}" and is ready to be shipped.`;
      case Constants.boxStatuses.shipped:
        return `The box has been shipped to customer's company "${box.contract?.company?.companyName}".`;
      case Constants.boxStatuses.delivered:
        return Constants.boxStatusesExplainedForLegend.delivered;
      case Constants.boxStatuses.mounted:
        return Constants.boxStatusesExplainedForLegend.mounted;
      case Constants.boxStatuses.available:
        return Constants.boxStatusesExplainedForLegend.available;
      case Constants.boxStatuses.unavailable:
        return Constants.boxStatusesExplainedForLegend.unavailable;
      case Constants.boxStatuses.inUse:
        return Constants.boxStatusesExplainedForLegend.inUse;
      case Constants.boxStatuses.fail:
        return Constants.boxStatusesExplainedForLegend.fail;
      case Constants.boxStatuses.toShipBack:
        return Constants.boxStatusesExplainedForLegend.toShipBack;
      case Constants.boxStatuses.goingHome:
        return Constants.boxStatusesExplainedForLegend.goingHome;
      default:
        return 'not implemented!';
    }

  }

  statusDetails(): void {
    this.disabled = true;
    this.dialog.open(StatusLegendDialogComponent, {data: {contract: false, box: true}})
      .afterClosed()
      .pipe()
      .subscribe(
        () => {
          this.disabled = false;
        },
        () => {
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  showBoxInfo(box: Box): void {
    this.dialog.open(BoxInterfaceInfoDialogComponent, {
      data: {
        contract: this.contract.id,
        box: box
      }
    });
  }

  protected deleteUserAction (box: Box) {
    return (userId: number): Observable<void> => this.usersService.deleteUserBox(userId, box.id);
  }
}
